// First signs of intent on a card (hover, touch, keyboard focus)
const MEDIA_LOAD_EVENTS = ['pointerenter', 'pointerdown', 'focusin'];
// Same srcset widths the swatch slides used when they were rendered in Liquid
const SWATCH_IMAGE_WIDTHS = [400, 800, 1200, 1600, 2000];

/** @param {*} value @returns {string} Safe for HTML text and double-quoted attributes */
const escapeHtml = (value) =>
  String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

/**
 * Starts loading a slide image that was rendered with data-src.
 * @param {HTMLImageElement} img
 */
const loadSlideImage = (img) => {
  if (!img.dataset.src) return;
  if (img.dataset.srcset) img.srcset = img.dataset.srcset;
  img.src = img.dataset.src;
  img.removeAttribute('data-srcset');
  img.removeAttribute('data-src');
};

/**
 * @param {number} count
 * @returns {Promise<void>} Resolves after that many painted frames
 */
const nextFrames = (count) =>
  new Promise((resolve) => {
    const step = (left) => (left ? window.requestAnimationFrame(() => step(left - 1)) : resolve());
    step(count);
  });

// Sneak peek: the first card of a [data-sneak-peek] grid previews its next image once, the first time it comes into view.
// Timings and travel match the Sept 2026 A/B test variant.
const SNEAK_PEEK_VISIBLE = 0.99;
const SNEAK_PEEK_RATIO = 0.3;
const SNEAK_PEEK_GAP = 5;
const SNEAK_PEEK_OUT_MS = 500;
const SNEAK_PEEK_HOLD_MS = 400;
const SNEAK_PEEK_BACK_MS = 500;
const SNEAK_PEEK_TOTAL_MS = SNEAK_PEEK_OUT_MS + SNEAK_PEEK_HOLD_MS + SNEAK_PEEK_BACK_MS;
const SNEAK_PEEK_IMAGE_WAIT_MS = 4000;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

export class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.onDelegatedClick = this.onDelegatedClick.bind(this);
    this.initMediaSwiper = this.initMediaSwiper.bind(this);
    this.onMediaResize = this.onMediaResize.bind(this);
    this.onSwatchClick = this.onSwatchClick.bind(this);
    this.onSwatchKeydown = this.onSwatchKeydown.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onCartClose = this.onCartClose.bind(this);
    this.loadMediaImages = this.loadMediaImages.bind(this);
    this.onSneakPeekIntersect = this.onSneakPeekIntersect.bind(this);
    this.onReduceMotionChange = this.onReduceMotionChange.bind(this);
    // Attach once — connectedCallback re-runs on re-parent, which would double-fire add-to-cart
    this.addEventListener('click', this.onDelegatedClick);
    for (const type of MEDIA_LOAD_EVENTS) {
      this.addEventListener(type, this.loadMediaImages);
    }
  }

  /* Slides 2+ render with data-src so they don't compete with the LCP image; load them on first intent. */
  loadMediaImages() {
    for (const type of MEDIA_LOAD_EVENTS) {
      this.removeEventListener(type, this.loadMediaImages);
    }

    for (const img of this.querySelectorAll('.card-media-swiper img[data-src]')) {
      loadSlideImage(img);
    }
  }

  connectedCallback() {
    this.observeMediaSwiper();
    this.bindSwatchListeners();
    this.observeSneakPeek();
    document.addEventListener('cart:close', this.onCartClose);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('cart:close', this.onCartClose);
    this.unobserveMediaSwiper();
    this.unobserveSneakPeek();
    this.unbindSwatchListeners();
    this.destroyMediaSwiper();
  }

  /** Only the first card of a [data-sneak-peek] grid (collection, search) previews its next image. */
  observeSneakPeek() {
    if (this.sneakPeekPlayed || this.sneakPeekObserver || reduceMotion.matches) return;

    const grid = this.closest('[data-sneak-peek]');
    const media = this.querySelector('.card-media-swiper');
    if (!grid || grid.querySelector('product-card') !== this) return;
    if (!media || media.querySelectorAll('.swiper-slide').length < 2) return;

    this.sneakPeekInView = false;
    this.sneakPeekObserver = new IntersectionObserver(this.onSneakPeekIntersect, { threshold: SNEAK_PEEK_VISIBLE });
    this.sneakPeekObserver.observe(media);
    reduceMotion.addEventListener('change', this.onReduceMotionChange);
    this.primeSneakPeek(media);
  }

  unobserveSneakPeek() {
    this.sneakPeekObserver?.disconnect();
    this.sneakPeekObserver = undefined;
    this.sneakPeekAnimation?.cancel();
    reduceMotion.removeEventListener('change', this.onReduceMotionChange);
    this.querySelector('.card-media-swiper')?.classList.remove('is-sneak-peek-card');
  }

  /**
   * Keeps the next slide drawn (still clipped outside the card) until the peek is over, and loads its
   * image once the page is idle. Safari leaves a slide blank if it turns visible as it starts to move.
   * @param {HTMLElement} media
   */
  primeSneakPeek(media) {
    media.classList.add('is-sneak-peek-card');

    const load = () => {
      const nextImage = media.querySelectorAll('.swiper-slide')[1]?.querySelector('img');
      if (this.isConnected && nextImage) this.imageReady(nextImage);
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(load, { timeout: 2000 });
    } else {
      window.setTimeout(load, 200);
    }
  }

  /** Turning on reduced motion mid-visit stops a running peek and any still to come. */
  onReduceMotionChange() {
    if (reduceMotion.matches) this.unobserveSneakPeek();
  }

  /**
   * Plays the peek as the card comes into view; the observer is dropped once it has played.
   * @param {IntersectionObserverEntry[]} entries
   */
  onSneakPeekIntersect(entries) {
    for (const entry of entries) {
      const inView = entry.isIntersecting && entry.intersectionRatio >= SNEAK_PEEK_VISIBLE - 0.01;
      if (inView && !this.sneakPeekInView) this.playSneakPeek();
      this.sneakPeekInView = inView;
    }
  }

  /**
   * Slides part of the way toward the next image and back. A preview, not a slide change:
   * the active image, pagination and arrows stay as they were.
   * @returns {Promise<void>}
   */
  async playSneakPeek() {
    if (this.sneakPeeking || reduceMotion.matches || this.matches(':hover')) return;

    this.initMediaSwiper();
    const swiper = this.mediaSwiper;
    if (!swiper || swiper.destroyed || swiper.animating || swiper.slides.length < 2) return;

    this.sneakPeeking = true;
    try {
      await this.runSneakPeek(swiper);
    } finally {
      this.sneakPeeking = false;
    }
  }

  /**
   * @param {object} swiper - The card's Swiper instance
   * @returns {Promise<void>}
   */
  async runSneakPeek(swiper) {
    // A blank sliver reads as broken, so the next image has to be ready first
    const nextImage = swiper.el.querySelector('.swiper-slide-next img');
    if (!nextImage || !(await this.imageReady(nextImage))) return;

    // Lay the gap out and let it paint before anything moves
    swiper.params.spaceBetween = SNEAK_PEEK_GAP;
    swiper.update();
    await nextFrames(2);

    if (
      !this.isConnected ||
      !this.sneakPeekInView ||
      swiper.destroyed ||
      swiper.animating ||
      reduceMotion.matches ||
      this.matches(':hover')
    ) {
      this.endSneakPeek(swiper);
      return;
    }

    // Once per card, as on live; a skipped attempt (hover, image still loading) retries on the next entry
    this.sneakPeekPlayed = true;
    this.sneakPeekObserver?.disconnect();
    this.sneakPeekObserver = undefined;

    // Moves the CSS translate property, which stacks on Swiper's transform, so update() calls mid-peek can't snap it back
    const shift = `${swiper.width * SNEAK_PEEK_RATIO * (swiper.rtlTranslate ? 1 : -1)}px 0`;
    this.sneakPeekAnimation = swiper.wrapperEl.animate(
      [
        { translate: '0 0', easing: 'ease-in-out' },
        { translate: shift, offset: SNEAK_PEEK_OUT_MS / SNEAK_PEEK_TOTAL_MS },
        { translate: shift, offset: (SNEAK_PEEK_OUT_MS + SNEAK_PEEK_HOLD_MS) / SNEAK_PEEK_TOTAL_MS, easing: 'ease-in-out' },
        { translate: '0 0' },
      ],
      SNEAK_PEEK_TOTAL_MS,
    );

    // A swipe or arrow click mid-peek takes over the slider
    const interrupt = () => this.sneakPeekAnimation?.cancel();
    swiper.on('touchStart', interrupt);
    swiper.on('realIndexChange', interrupt);
    await this.sneakPeekAnimation.finished.catch(() => {});

    swiper.off('touchStart', interrupt);
    swiper.off('realIndexChange', interrupt);
    this.sneakPeekAnimation = undefined;
    reduceMotion.removeEventListener('change', this.onReduceMotionChange);
    swiper.el.classList.remove('is-sneak-peek-card');
    this.endSneakPeek(swiper);
  }

  /**
   * Puts the slides back to their resting spacing.
   * @param {object} swiper
   */
  endSneakPeek(swiper) {
    if (swiper.destroyed) return;

    swiper.params.spaceBetween = 0;
    swiper.update();
  }

  /**
   * Loads a lazy image now and resolves once it can paint, or false if it is still loading after a short wait.
   * @param {HTMLImageElement} image
   * @returns {Promise<boolean>}
   */
  async imageReady(image) {
    image.loading = 'eager';
    loadSlideImage(image);
    if (image.complete && image.naturalWidth > 0) return true;

    const timeout = new Promise((resolve) => {
      window.setTimeout(() => resolve(false), SNEAK_PEEK_IMAGE_WAIT_MS);
    });
    const decoded = image.decode().then(
      () => true,
      () => false,
    );
    return Promise.race([decoded, timeout]);
  }

  /* Keep the size picker open while the cart drawer is open (live parity).
     Close it only when the drawer itself closes. */
  onCartClose() {
    const modal = this.querySelector('.card-quick-add__modal--open');
    if (modal) this.closeModal(modal);
  }

  /* Initializing a Swiper per card is expensive; with dozens of cards that
     blocks the main thread for seconds (high TBT). Defer each init until the
     card approaches the viewport, then run it in an idle slice so several
     cards entering at once don't create one long task. */
  observeMediaSwiper() {
    const el = this.querySelector('.card-media-swiper');
    if (!el || el.classList.contains('swiper-initialized') || this.mediaObserver) return;

    this.mediaObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.unobserveMediaSwiper();
        const init = () => {
          if (this.isConnected) this.initMediaSwiper();
        };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(init, { timeout: 500 });
        } else {
          window.setTimeout(init, 50);
        }
      },
      { rootMargin: '200px' }
    );
    this.mediaObserver.observe(el);

    // Someone who scrolls straight to a card and reaches for the arrows would
    // otherwise wait out the idle slice, so any pointer on the card inits now.
    this.addEventListener('pointerenter', this.initMediaSwiper);
    this.addEventListener('pointerdown', this.initMediaSwiper);
  }

  unobserveMediaSwiper() {
    this.mediaObserver?.disconnect();
    this.mediaObserver = undefined;
  }

  destroyMediaSwiper() {
    this.mediaResizeObserver?.disconnect();
    this.mediaResizeObserver = undefined;

    if (!this.mediaSwiper) return;

    this.mediaSwiper.destroy(true, true);
    this.mediaSwiper = undefined;
  }

  onDelegatedClick(event) {
    // Size options in the modal, plus the single-variant "+" (a .card-quick-add__submit outside any modal).
    if (event.target.closest('.card-quick-add__size-option, .card-quick-add__submit')) {
      this.onAddToCartClick(event);
      return;
    }

    if (event.target.closest('.card-quick-add__button')) {
      this.onModalOpenClick(event);
      return;
    }

    if (event.target.closest('.card-quick-add__close') || event.target.closest('.card-quick-add__overlay')) {
      this.onModalCloseClick(event);
    }
  }

  bindSwatchListeners() {
    if (this.swatchListenersBound) return;

    for (const swatch of this.querySelectorAll('.color-swatch')) {
      this.bindSwatch(swatch);
    }

    this.swatchListenersBound = true;
  }

  /** @param {HTMLElement} swatch - A role="button" colour swatch */
  bindSwatch(swatch) {
    swatch.addEventListener('click', this.onSwatchClick);
    swatch.addEventListener('keydown', this.onSwatchKeydown);
  }

  /**
   * Enter and Space select a swatch, as they would a native button.
   * @param {KeyboardEvent} event
   */
  onSwatchKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    // Space would otherwise scroll the page
    event.preventDefault();
    this.onSwatchClick(event);
  }

  /**
   * Stamps the quick-add dialog from its <template> on first use, so closed dialogs add no DOM nodes.
   * @returns {void}
   */
  renderQuickAdd() {
    const template = this.querySelector('template.card-quick-add__template');
    if (!template) return;

    const content = template.content.cloneNode(true);

    for (const swatch of content.querySelectorAll('.color-swatch')) {
      this.bindSwatch(swatch);
    }
    template.replaceWith(content);
  }

  unbindSwatchListeners() {
    if (!this.swatchListenersBound) return;

    for (const swatch of this.querySelectorAll('.color-swatch')) {
      swatch.removeEventListener('click', this.onSwatchClick);
      swatch.removeEventListener('keydown', this.onSwatchKeydown);
    }

    this.swatchListenersBound = false;
  }

  initMediaSwiper() {
    this.removeEventListener('pointerenter', this.initMediaSwiper);
    this.removeEventListener('pointerdown', this.initMediaSwiper);

    const el = this.querySelector('.card-media-swiper');
    if (!el || el.classList.contains('swiper-initialized')) return;

    // swiper14.0.6.min.js is a deferred head script, so it always executes
    // before this module; the guard is only a safety net.
    if (typeof Swiper === 'undefined') return;

    const slideCount = el.querySelectorAll('.swiper-slide').length;
    if (slideCount === 0) return;

    // Plain Swiper navigation, exactly like the live <swiper-container>.
    // loopPreventsSliding stays at its default (true) — this is the key to the
    // Safari fix: while a transition is animating, slideNext/Prev returns
    // BEFORE running loopFix(), so a mid-transition click never repositions the
    // wrapper (that reposition was the Safari double-click stutter). Clicks
    // that land after a transition completes advance normally.
    this.mediaSwiper = new Swiper(el, {
      loop: slideCount > 1,
      speed: 200,
      effect: 'slide',
      cssMode: false,
      pagination: {
        el: el.querySelector('.swiper-pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
    });

    // Swiper re-measures a frame after the card resizes (filters panel, grid view), so stale slide widths
    // flash as zoomed/cropped images. Updating from our own observer fixes the size before paint.
    if ('ResizeObserver' in window) {
      this.mediaResizeObserver = new ResizeObserver(this.onMediaResize);
      this.mediaResizeObserver.observe(el);
    }
  }

  /**
   * Re-measures the media swiper in the same frame the card changes width.
   * @param {ResizeObserverEntry[]} entries
   */
  onMediaResize(entries) {
    const swiper = this.mediaSwiper;
    if (!swiper || swiper.destroyed) return;

    for (const entry of entries) {
      if (Math.round(entry.contentRect.width) !== Math.round(swiper.width)) swiper.update();
    }
  }

  onModalOpenClick(event) {
    event.stopPropagation();
    event.preventDefault();
    // Only one quick view at a time (live parity); the owning card also drops its outside-click listener
    for (const openModal of document.querySelectorAll('.card-quick-add__modal.card-quick-add__modal--open')) {
      (openModal.closest('product-card') ?? this).closeModal(openModal);
    }

    this.renderQuickAdd();
    const modal = this.querySelector('.card-quick-add__modal');
    if (modal) {
      this.resetModal(modal);
      modal.classList.add('card-quick-add__modal--open');
      document.addEventListener('click', this.onOutsideClick);
      // Cards can sit inside a horizontal scroller (featured releases track);
      // a scrolling focus would drag that track and the page to the dialog.
      modal.querySelector('.card-quick-add__content')?.focus({ preventScroll: true });
    }
  }

  onModalCloseClick(event) {
    event.stopPropagation();
    event.preventDefault();
    const modal =
      event.target.closest('.card-quick-add__modal') ||
      this.querySelector('.card-quick-add__modal.card-quick-add__modal--open');
    this.closeModal(modal);
  }

  onOutsideClick(event) {
    if (event.target.closest('.card-quick-add__content')) return;
    // Don't dismiss the size picker while the user is interacting with the cart
    if (document.querySelector('cart-drawer.cart-open')) return;
    this.closeModal(this.querySelector('.card-quick-add__modal--open'));
  }

  closeModal(modal) {
    document.removeEventListener('click', this.onOutsideClick);
    if (!modal) return;
    modal.classList.remove('card-quick-add__modal--open');
    this.resetModal(modal);
  }

  resetModal(modal) {
    if (!modal) return;

    const addedText = modal.querySelector('.card-quick-add__success');
    if (addedText) addedText.style.display = 'none';

    const loader = modal.querySelector('.card-quick-add__loader');
    const sizeWrap = modal.querySelector('.card-quick-add__size-list');
    this.setLoadingState({ loader, sizeWrap }, false);

    for (const radio of modal.querySelectorAll('.card-quick-add__size-input:checked')) {
      radio.checked = false;
    }

    for (const btn of modal.querySelectorAll('.is-loading')) {
      btn.classList.remove('is-loading');
    }
  }

  onAddToCartClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const sizeOption = event.target.closest('.card-quick-add__size-option');
    const addToCartBtn =
      event.target.closest('.card-quick-add__submit') ||
      sizeOption?.querySelector('.card-quick-add__submit');
    if (!addToCartBtn || addToCartBtn.disabled || addToCartBtn.classList.contains('is-loading')) return;
    addToCartBtn.checked = true;
    // cart.js is a deferred head script, so window.Cart always exists by the
    // time a user can click; navigation is already prevented above either way.
    if (!window.Cart?.add) return;

    const { preOrderProduct, finalSale, newSizeVariant } = addToCartBtn.dataset;

    const modal = addToCartBtn.closest('.card-quick-add__modal');
    const elements = {
      addedText: modal?.querySelector('.card-quick-add__success'),
      loader: modal?.querySelector('.card-quick-add__loader'),
      sizeWrap: modal?.querySelector('.card-quick-add__size-list'),
    };

    addToCartBtn.classList.add('is-loading');
    if (elements.addedText) elements.addedText.style.display = 'none';
    this.setLoadingState(elements, true);

    const formData = new FormData();
    formData.append('id', newSizeVariant);
    formData.append('quantity', '1');

    const properties = this.getProductProperties(preOrderProduct, finalSale);
    if (properties) {
      for (const [key, value] of Object.entries(properties)) {
        formData.append(`properties[${key}]`, value);
      }
    }

    window.Cart.add(formData, addToCartBtn)
      .then(() => {
        // Match live: leave the size picker open and show "ADDED TO BAG".
        // It closes when the cart drawer fires cart:close.
        if (elements.addedText) elements.addedText.style.display = 'block';
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
      })
      .finally(() => {
        this.setLoadingState(elements, false);
        addToCartBtn.classList.remove('is-loading');
      });
  }

  getProductProperties(preOrderProduct, finalSale) {
    if (preOrderProduct === 'preorder-product') {
      return { '_PRE ORDER': ' ' };
    }
    if (finalSale === 'final-sale') {
      return { '_FINAL SALE': ' ' };
    }
    return null;
  }

  setLoadingState(elements, isLoading) {
    const { sizeWrap, loader } = elements;
    if (sizeWrap) {
      sizeWrap.style.opacity = isLoading ? '0.3' : '1';
      sizeWrap.style.pointerEvents = isLoading ? 'none' : 'auto';
    }
    if (loader) {
      loader.style.display = isLoading ? 'flex' : 'none';
    }
  }


  /**
   * Builds swiper slide markup from the compact swatch image JSON (component-product-swatch-images.liquid).
   * @param {string} json - [{ src, alt, width, height }]
   * @returns {string[]} Slide HTML, same markup the card renders server-side
   */
  buildSwatchSlides(json) {
    let images = [];
    try {
      images = JSON.parse(json);
    } catch (error) {
      console.error('Invalid swatch image data', error);
      return [];
    }

    return images.map((image, index) => {
      const srcset = SWATCH_IMAGE_WIDTHS.map((width) => `${image.src}&width=${width} ${width}w`).join(', ');
      return `<div class="swiper-slide"><img loading="${index === 0 ? 'eager' : 'lazy'}" src="${escapeHtml(image.src)}&width=1000" alt="${escapeHtml(image.alt)}" width="${escapeHtml(image.width)}" height="${escapeHtml(image.height)}" srcset="${escapeHtml(srcset)}" sizes="(max-width: 749px) 50vw, 25vw"></div>`;
    });
  }

  /**
   * Builds the quick-add size radios from the compact swatch variant JSON (component-product-swatch-variants.liquid).
   * @param {string} json - { product, finalSale, preorder, variants: [[id, size, available]] }
   * @returns {string|null} Size list HTML, same markup the card renders server-side; null on bad data
   */
  buildSwatchSizes(json) {
    let data;
    try {
      data = JSON.parse(json);
    } catch (error) {
      console.error('Invalid swatch variant data', error);
      return null;
    }

    if (!Array.isArray(data?.variants)) return null;

    const finalSale = data.finalSale ? 'final-sale' : '';
    const preorder = data.preorder ? 'preorder-product' : '';
    return data.variants
      .map(([id, size, available]) => {
        const variantId = escapeHtml(id);
        return `<label class="card-quick-add__size-option${available ? '' : ' card-quick-add__size-option--disabled'}"><input type="radio" class="card-quick-add__size-input card-quick-add__submit" name="card-quick-add-size-${escapeHtml(data.product)}" value="${variantId}" data-new-size-variant="${variantId}" data-variant-available="${Boolean(available)}" data-final-sale="${finalSale}" data-pre-order-product="${preorder}"${available ? '' : ' disabled'}><span class="card-quick-add__size-text">${escapeHtml(size)}</span></label>`;
      })
      .join('');
  }

  onSwatchClick(event) {
    // The dialog shows the picked colour when it opens, so it needs to exist before the sync below
    this.renderQuickAdd();
    const swatch = event.currentTarget;
    const swiperContainer = this.querySelector('.card-media-child .card-media-swiper');
    const productAddBtn = this.querySelector('.card-quick-add');
    const productPrice = this.querySelector('.card-pricing');
    const productVariants = this.querySelector('.card-quick-add__size-list');
    const productUrl = this.querySelector('.card-quick-add__link');
    const productPopupImage = this.querySelector('.card-quick-add__media img');
    const cardMediaUrl = this.querySelector('.card-media');
    const cardTitleUrl = this.querySelector('.card-heading a');
    const swatchUrl = swatch.dataset.swatchUrl;
    // Quick-add swatches carry no payload when the card's swatch row has it; read it from the matching card swatch
    const payload =
      swatch.dataset.swatchImages !== undefined
        ? swatch
        : [...this.querySelectorAll('.color-swatch[data-swatch-images]')].find((s) => s.dataset.swatchUrl === swatchUrl) || swatch;
    const swatchImages = payload.dataset.swatchImages;
    const swatchPrice = payload.dataset.swatchPrice;
    const swatchAddBtn = payload.dataset.swatchAddBtn;
    const swatchVariants = payload.dataset.swatchVariants;
    const swatchPopupImage = swatch.dataset.swatchPopupImage;
    const swatchColorName = swatch.dataset.swatchColorName;
    const colorLabel = this.querySelector('.card-quick-add__color-value');

    // Update selected state (card + modal swatch sets)
    const variantIdKey = swatch.dataset.variantId;
    for (const s of this.querySelectorAll('.color-swatch')) {
      const isMatch =
        (swatchUrl && s.dataset.swatchUrl === swatchUrl) ||
        (variantIdKey && s.dataset.variantId === variantIdKey);
      s.classList.toggle('selected', Boolean(isMatch));
    }

    if (colorLabel && swatchColorName) {
      colorLabel.textContent = swatchColorName;
    }

    // The swiper is lazy-initialized; make sure it exists before swapping slides
    if (swiperContainer && swatchImages && !this.mediaSwiper) {
      this.unobserveMediaSwiper();
      this.initMediaSwiper();
    }

    // Update product image - update slides instead of replacing entire slider
    if (swiperContainer && swatchImages && this.mediaSwiper) {
      const swiper = this.mediaSwiper;

      swiperContainer.classList.add('swiper-loading');

      // Rebuild slides for the selected color (destroy loop clones first)
      const slidesArray = this.buildSwatchSlides(swatchImages);
      if (typeof swiper.loopDestroy === 'function') swiper.loopDestroy();
      swiper.removeAllSlides();
      swiper.appendSlide(slidesArray);
      swiper.update();
      if (slidesArray.length > 1 && typeof swiper.loopCreate === 'function') {
        swiper.loopCreate();
        swiper.update();
      }
      if (typeof swiper.slideToLoop === 'function') {
        swiper.slideToLoop(0, 0);
      } else {
        swiper.slideTo(0, 0);
      }
      swiperContainer.classList.remove('swiper-loading');
    }
    if (productAddBtn && swatchAddBtn) {
      productAddBtn.innerHTML = swatchAddBtn;
    }
    if (productPrice && swatchPrice) {
      productPrice.innerHTML = swatchPrice;
    }
    const sizesHtml = swatchVariants ? this.buildSwatchSizes(swatchVariants) : null;
    if (productVariants && sizesHtml !== null) {
      productVariants.innerHTML = sizesHtml;
    }
    if (productUrl && swatchUrl) {
      productUrl.href = swatchUrl;
    }
    if (swatchUrl) {
      cardMediaUrl.href = swatchUrl;
      cardTitleUrl.href = swatchUrl;
    }

    if (productPopupImage && swatchPopupImage) {
      productPopupImage.src = swatchPopupImage;
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
